import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

const GAP_PX = 4;          // gap between items (matches space-x-1)
const MORE_BTN_PX = 88;    // safe reserve for the "Mais ▾" button

/**
 * Responsive horizontal nav:
 *  - Renders as many items as fit in the available width
 *  - Overflow goes into a "Mais ▾" dropdown
 *  - Re-measures on ResizeObserver (reacts to zoom, window resize, sidebar changes)
 */
function ResponsiveNav({ items, isActive }) {
  const location = useLocation();
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(items.length);

  // Compute how many items fit whenever container width changes
  const recompute = useCallback(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const available = container.clientWidth;
    const children = Array.from(measure.children);
    if (children.length === 0) return;

    // cumulative widths including gaps
    const widths = children.map(c => c.getBoundingClientRect().width);
    const total = widths.reduce((a, b) => a + b, 0) + GAP_PX * (widths.length - 1);

    if (total <= available) {
      setVisibleCount(items.length);
      return;
    }

    // Need to show "Mais ▾" — reserve space for it
    const budget = available - MORE_BTN_PX - GAP_PX;
    let acc = 0;
    let count = 0;
    for (let i = 0; i < widths.length; i++) {
      const next = acc + widths[i] + (i > 0 ? GAP_PX : 0);
      if (next > budget) break;
      acc = next;
      count++;
    }
    setVisibleCount(Math.max(0, count));
  }, [items.length]);

  // Measure on mount, items change, and ANY container resize (zoom, window, sidebar)
  useLayoutEffect(() => {
    recompute();
  }, [recompute, items]);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(() => recompute());
    ro.observe(containerRef.current);
    window.addEventListener('resize', recompute);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', recompute);
    };
  }, [recompute]);

  // Re-measure whenever the route changes (active style adds font-weight which can shift widths)
  useEffect(() => {
    recompute();
  }, [location.pathname, recompute]);

  const visibleItems = items.slice(0, visibleCount);
  const overflowItems = items.slice(visibleCount);

  const itemClass = (active) => `px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors whitespace-nowrap ${
    active ? 'bg-emerald-100 text-emerald-800 font-medium' : 'text-gray-700 hover:bg-gray-100'
  }`;

  const renderIcon = (d) => (
    <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
    </svg>
  );

  return (
    <div className="relative w-full">
      {/* Hidden measurement layer — renders ALL items to capture their natural widths */}
      <div
        ref={measureRef}
        aria-hidden
        className="invisible absolute -top-[9999px] left-0 flex items-center space-x-1 pointer-events-none"
      >
        {items.map(item => (
          <div key={`m-${item.path}`} className={itemClass(false)}>
            {renderIcon(item.icon)}
            <span>{item.label}</span>
          </div>
        ))}
      </div>

      {/* Actual visible nav */}
      <div
        ref={containerRef}
        className="flex items-center justify-center space-x-1 py-2 w-full"
      >
        {visibleItems.map(item => (
          <Link
            key={item.path}
            to={item.path}
            className={itemClass(isActive(item.path))}
            data-testid={`nav-${item.path.substring(1)}`}
          >
            {renderIcon(item.icon)}
            <span>{item.label}</span>
          </Link>
        ))}

        {overflowItems.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`px-3 py-2 rounded-lg flex items-center space-x-1 transition-colors whitespace-nowrap ${
                  overflowItems.some(i => isActive(i.path))
                    ? 'bg-emerald-100 text-emerald-800 font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                data-testid="nav-more-button"
                aria-label="Mais opções do menu"
              >
                <span>Mais</span>
                <ChevronDown className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[200px]">
              {overflowItems.map(item => (
                <DropdownMenuItem
                  key={item.path}
                  asChild
                  className={isActive(item.path) ? 'bg-emerald-50 text-emerald-800 font-medium' : ''}
                >
                  <Link
                    to={item.path}
                    className="flex items-center space-x-2 w-full cursor-pointer"
                    data-testid={`nav-more-${item.path.substring(1)}`}
                  >
                    {renderIcon(item.icon)}
                    <span>{item.label}</span>
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}

export default ResponsiveNav;
